import React, { useState } from 'react'
import {
  Controller,
  ControllerRenderProps,
  useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Dropdown,
  FormControl,
  InputGroup,
  TextareaInput,
  Loader,
  TicketImageUpload,
} from 'components'
import { findSelected, trimFirstSpace } from 'helpers/common'
import {
  useFindAllTaskIssue,
  filterOptionBuilder,
  ISchemaFormCloseTicketPayload,
} from 'features/Ticket'

interface RequestCloseModalProps {
  backupPayloadCloseTask: (key: string, value: string) => void
  payloadForCloseTask: ISchemaFormCloseTicketPayload
  setPayloadForCloseTask: (payload: ISchemaFormCloseTicketPayload) => void
}

function TicketRequestCloseModalForm(
  props: RequestCloseModalProps,
): JSX.Element {
  const {
    backupPayloadCloseTask = () => {},
    payloadForCloseTask,
    setPayloadForCloseTask,
  } = props

  const [taskIssueChecking, setTaskIssueCheckingChecking] =
    useState<boolean>(false)

  const { t } = useTranslation('ticket')

  const { control, errors, setValue, trigger, clearErrors } = useFormContext()
  const [{ data: taskIssue, loading: taskIssueLoading }] = useFindAllTaskIssue()

  const base = 'f-ticket-request-close-modal'

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    fields: ControllerRenderProps<Record<string, File>>,
  ) => {
    const files = Array.from(event.target.files || [])
    const currentImages = Array.isArray(fields.value) ? fields.value : []
    const newImages = [...currentImages, ...files]
    const limitedImages = newImages.slice(0, 5)

    fields.onChange(limitedImages)
    setPayloadForCloseTask({
      ...payloadForCloseTask,
      completedPhoto: limitedImages,
    })
  }

  const handleImageClear = (
    index: number,
    fields: ControllerRenderProps<Record<string, File>>,
  ) => {
    const newImages = fields.value.filter((_: File, i: number) => i !== index)

    fields.onChange(newImages)
    setPayloadForCloseTask({
      ...payloadForCloseTask,
      completedPhoto: newImages,
    })
  }

  return (
    <div className={`${base}__form`}>
      <FormControl label={t('issueType.title')} required>
        <InputGroup
          error={errors.completedTaskIssue?.message}
          variants='secondary'
        >
          <Controller
            type='text'
            name='completedTaskIssue'
            control={control}
            isError={Boolean(errors.completedTaskIssue?.message)}
            message={errors.completedTaskIssue?.message}
            render={(field) => (
              <Dropdown
                className={`${base}__form-item`}
                placeholder={t('selectIssueType.placeholder')}
                withIcon={(taskIssueLoading || taskIssueChecking) && <Loader />}
                isError={Boolean(errors.completedTaskIssue?.message)}
                options={filterOptionBuilder(taskIssue)}
                onChange={(value) => {
                  backupPayloadCloseTask('completedTaskIssue', value.key)
                  setValue('completedTaskIssue', value.key, {
                    shouldDirty: true,
                  })
                  clearErrors('completedTaskIssue')
                }}
                onBlur={async () => {
                  setTaskIssueCheckingChecking(true)
                  await trigger('taskIssue')
                  setTaskIssueCheckingChecking(false)
                }}
                selected={findSelected(
                  filterOptionBuilder(taskIssue),
                  field.value,
                )}
              />
            )}
          />
        </InputGroup>
      </FormControl>

      <FormControl label={t('remark.title')}>
        <Controller
          name='completedDetail'
          control={control}
          render={(fields) => (
            <TextareaInput
              placeholder={t('insertDetail.placeholder')}
              className={`${base}__form-item`}
              hasCountable
              {...fields}
              onChange={(e) => {
                backupPayloadCloseTask('completedDetail', trimFirstSpace(e))
                fields.onChange(trimFirstSpace(e))
              }}
            />
          )}
        />
      </FormControl>

      <FormControl label={t('images.title')} recommend={t('images.recommend')}>
        <Controller
          name='completedPhoto'
          control={control}
          defaultValue={null}
          render={(fields) => {
            return (
              <TicketImageUpload
                images={fields.value || []}
                onAddImages={(event) => handleImageChange(event, fields)}
                onRemoveImage={(index) => handleImageClear(index, fields)}
                className={`${base}__form-item`}
              />
            )
          }}
        />
      </FormControl>
    </div>
  )
}

export default TicketRequestCloseModalForm
